import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Tabs,
  Tab,
  Paper,
  Select,
  MenuItem,
  TextareaAutosize,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControl,
  InputLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  IconButton,
  LinearProgress,
  Slider,
  Card,
  CardContent,
  Tooltip,
  Alert,
  Snackbar
} from '@mui/material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import { useCompany } from '../contexts/CompanyContext';
import { useCompanyMembership } from '../contexts/CompanyMembershipContext';
import { ensureHttps, logUrl } from '../utils/urlUtils';
import { functions } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { ensurePortalTokenForEstimate, ensureSigningTokenForEstimate, buildPortalUrl } from '../utils/estimateUtils';
import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DeleteIcon from '@mui/icons-material/Delete';
import BuildIcon from '@mui/icons-material/Build';
import PaymentIcon from '@mui/icons-material/Payment';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ScheduleIcon from '@mui/icons-material/Schedule';
import InventoryIcon from '@mui/icons-material/Inventory';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveIcon from '@mui/icons-material/Remove';
import TimelineIcon from '@mui/icons-material/Timeline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const JOB_STATUSES = [
  'Not Started',
  'In Progress',
  'On Hold',
  'Completed',
  'Cancelled'
];

const PAYMENT_METHODS = [
  'Cash',
  'Card',
  'Check',
  'Transfer'
];

const WORK_STATUS_OPTIONS = [
  'Not Started',
  'In Progress',
  'On Hold',
  'Completed'
];

const WORK_ITEM_STATUSES = [
  'Not Started',
  'In Progress',
  'Completed',
  'On Hold'
];

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ padding: '24px 0' }}>
      {value === index && children}
    </div>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { jobs, updateJob, deleteJob, estimates, teams, inventory, inventoryTransactions, checkoutInventoryItem, checkinInventoryItem } = useData();
  const { companyProfile } = useCompany();
  const { companyId } = useCompanyMembership();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(0);
  const [newNote, setNewNote] = useState('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isWorkDialogOpen, setIsWorkDialogOpen] = useState(false);
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const invoiceRef = useRef(null);
  const [newWork, setNewWork] = useState({
    name: '',
    description: '',
    status: 'Not Started',
    scheduledDate: new Date()
  });
  const [selectedEstimateId, setSelectedEstimateId] = useState('');
  const [selectedLineItem, setSelectedLineItem] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [job, setJob] = useState(null);
  const [openWorkItemDialog, setOpenWorkItemDialog] = useState(false);
  const [editingWorkItem, setEditingWorkItem] = useState(null);
  const [newWorkItem, setNewWorkItem] = useState({
    name: '',
    description: '',
    quantity: 1,
    unitPrice: 0,
    status: 'Not Started'
  });
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [newPayment, setNewPayment] = useState({
    amount: 0,
    method: 'Cash',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [viewMode, setViewMode] = useState('list');
  const [dependencyDialogOpen, setDependencyDialogOpen] = useState(false);
  const [selectedWorkItem, setSelectedWorkItem] = useState(null);
  const [selectedDependency, setSelectedDependency] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [scheduledDate, setScheduledDate] = useState(new Date());
  const [schedulingNotes, setSchedulingNotes] = useState('');
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [checkinDialogOpen, setCheckinDialogOpen] = useState(false);
  const [selectedInventoryItem, setSelectedInventoryItem] = useState('');
  const [checkoutQuantity, setCheckoutQuantity] = useState(0);
  const [checkinQuantity, setCheckinQuantity] = useState(0);
  const [checkoutNotes, setCheckoutNotes] = useState('');
  const [checkinNotes, setCheckinNotes] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [portalLinkCopied, setPortalLinkCopied] = useState(false);
  const [creatorName, setCreatorName] = useState('');

  useEffect(() => {
    const foundJob = jobs.find(j => j.id === id);
    if (foundJob) {
      setJob(foundJob);
    } else {
      navigate('/jobs');
    }
  }, [id, jobs, navigate]);

  // Fetch creator name from Firestore
  useEffect(() => {
    const fetchCreatorName = async () => {
      if (!job?.createdBy || !companyId) {
        return;
      }

      try {
        // Try company users subcollection first
        const companyUserRef = doc(db, 'companies', companyId, 'users', job.createdBy);
        const companyUserDoc = await getDoc(companyUserRef);
        
        if (companyUserDoc.exists()) {
          const userData = companyUserDoc.data();
          setCreatorName(userData.name || userData.email || job.createdBy);
          return;
        }

        // Fallback to top-level users collection
        const userRef = doc(db, 'users', job.createdBy);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCreatorName(userData.name || userData.email || job.createdBy);
        } else {
          // If no user doc found, check if createdBy is already an email/name
          setCreatorName(job.createdBy);
        }
      } catch (error) {
        console.error('[JobDetail] Error fetching creator name:', error);
        setCreatorName(job.createdBy);
      }
    };

    if (job?.createdBy) {
      fetchCreatorName();
    }
  }, [job?.createdBy, companyId]);

  // Handle payment success/cancel redirect from Stripe
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      // Payment successful - reload job data to get updated status
      const foundJob = jobs.find(j => j.id === id);
      if (foundJob) {
        setJob(foundJob);
      }
      // Remove query param
      searchParams.delete('payment');
      setSearchParams(searchParams);
    } else if (paymentStatus === 'cancelled') {
      // Payment cancelled
      searchParams.delete('payment');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams, id, jobs]);

  // Handle Pay Online button click
  const handlePayOnline = async () => {
    if (!job || !companyId) {
      setPaymentError('Unable to process payment. Please refresh the page.');
      return;
    }

    setIsProcessingPayment(true);
    setPaymentError('');

    try {
      console.log('[payments] Creating checkout session for job:', job.id);
      
      // Call Cloud Function to create Stripe Checkout Session
      const createCheckoutSession = httpsCallable(functions, 'createInvoiceCheckoutSession');
      const result = await createCheckoutSession({
        jobId: job.id,
        companyId: companyId,
      });

      const { url } = result.data;

      if (!url) {
        throw new Error('No checkout URL returned from server');
      }

      console.log('[payments] Redirecting to Stripe Checkout:', url);
      
      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (error) {
      console.error('[payments] Error creating checkout session:', error);
      setPaymentError(
        error.message || 'Failed to start payment process. Please try again.'
      );
      setIsProcessingPayment(false);
    }
  };

  // Handle Copy Portal Link button click
  const handleCopyPortalLink = async () => {
    if (!job || !job.estimateId || !companyId) {
      alert('Unable to generate portal link. Please ensure this job has an associated estimate.');
      return;
    }

    try {
      if (!companyId) {
        alert('Company ID not available. Please refresh the page and try again.');
        return;
      }

      // Find the estimate to get its details
      const estimate = estimates.find(e => e.id === job.estimateId);
      if (!estimate) {
        alert('Associated estimate not found. Please ensure the estimate exists.');
        return;
      }

      // Ensure both portal token and signing token exist (portal serves as signing link)
      const [portalToken, signingToken] = await Promise.all([
        ensurePortalTokenForEstimate(companyId, job.estimateId),
        ensureSigningTokenForEstimate(companyId, job.estimateId)
      ]);
      
      // Build portal URL
      const portalUrl = buildPortalUrl(portalToken);

      // Copy to clipboard
      await navigator.clipboard.writeText(portalUrl);
      setPortalLinkCopied(true);
      setTimeout(() => setPortalLinkCopied(false), 3000);

      // Show success message with option to email
      const userConfirmed = window.confirm(
        `Customer Portal link copied to clipboard!\n\nShare this link with your customer:\n${portalUrl}\n\nYour customer can view, download, and sign the estimate from this portal.\n\nClick OK to open email client, or Cancel to just copy the link.`
      );
      
      if (userConfirmed && estimate.contactInfo?.email) {
        const subject = encodeURIComponent(`Your Estimate #${estimate.estimateId || estimate.id} - Customer Portal`);
        const body = encodeURIComponent(
          `Hello,\n\nPlease review your estimate #${estimate.estimateId || estimate.id} in the customer portal:\n${portalUrl}\n\nYou can view the estimate details, download a PDF, and sign it directly from the portal.\n\nThank you!`
        );
        window.location.href = `mailto:${estimate.contactInfo.email}?subject=${subject}&body=${body}`;
      }

      console.log('[job] Portal link copied:', portalUrl);
    } catch (error) {
      console.error('[job] Error copying portal link:', error);
      alert('Failed to copy portal link. Please try again.');
    }
  };

  if (!job) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5">Job not found</Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/jobs')}
          sx={{ mt: 2 }}
        >
          Back to Jobs
        </Button>
      </Box>
    );
  }

  // Find related estimate and its line items
  const estimate = job.estimateId ? estimates.find(e => e.id === job.estimateId) : null;
  const lineItems = estimate?.lineItems || [];

  // Get all estimates for this job
  const jobEstimates = estimates.filter(e => e.jobId === job.id);

  const handleStatusChange = (newStatus) => {
    if (!job) return;
    const updatedJob = { ...job, status: newStatus };
    updateJob(job.id, updatedJob);
    setJob(updatedJob);
  };

  const handleWorkItemStatusChange = (index, newStatus) => {
    if (!job) return;
    const updatedWorkItems = [...job.workItems];
    updatedWorkItems[index] = { 
      ...updatedWorkItems[index], 
      status: newStatus
    };
    const updatedJob = { ...job, workItems: updatedWorkItems };
    updateJob(job.id, updatedJob);
    setJob(updatedJob);
  };

  const handleEstimateChange = (e) => {
    const estimateId = e.target.value;
    setSelectedEstimateId(estimateId);
    setSelectedLineItem('');
    
    if (estimateId === 'custom') {
      setNewWorkItem({
        name: '',
        description: '',
        quantity: 1,
        unitPrice: 0,
        status: 'Not Started'
      });
    }
  };

  const handleLineItemChange = (e) => {
    const value = e.target.value;
    setSelectedLineItem(value);
    
    if (value === 'custom') {
      setNewWorkItem({
        name: '',
        description: '',
        quantity: 1,
        unitPrice: 0,
        status: 'Not Started'
      });
    } else {
      const selectedEstimate = estimates.find(e => e.id === selectedEstimateId);
      const item = selectedEstimate?.lineItems?.[parseInt(value)];
      if (item) {
        setNewWorkItem({
          name: item.name || '',
          description: item.description || '',
          quantity: item.quantity || 1,
          unitPrice: item.unitPrice || 0,
          status: 'Not Started'
        });
      }
    }
  };

  const handleScheduleWork = async () => {
    if (selectedLineItem === '' || selectedTeam === '' || !scheduledDate) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      // Convert selectedLineItem to number if it's a string
      const lineItemIndex = typeof selectedLineItem === 'string' ? parseInt(selectedLineItem, 10) : selectedLineItem;
      
      // Check if this line item is already scheduled
      const isAlreadyScheduled = job.workItems?.some(
        item => item.estimateId === job.estimateId && item.lineItemIndex === lineItemIndex
      );

      if (isAlreadyScheduled) {
        alert('This line item is already scheduled');
        return;
      }

      // Get the line item details from the estimate
      const lineItem = estimate?.lineItems[lineItemIndex];
      if (!lineItem) {
        alert('Could not find line item details');
        return;
      }

      // Create new work item
      const newWorkItem = {
        id: Date.now().toString(),
        name: lineItem.name,
        description: lineItem.description,
        quantity: lineItem.quantity,
        unitPrice: lineItem.unitPrice,
        estimateId: job.estimateId,
        lineItemIndex: lineItemIndex,
        team: selectedTeam,
        scheduledDate: scheduledDate.toISOString(),
        status: 'Scheduled',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Update job with new work item
      const updatedWorkItems = [...(job.workItems || []), newWorkItem];
      const updatedJob = { ...job, workItems: updatedWorkItems, updatedAt: new Date().toISOString() };
      await updateJob(job.id, updatedJob);
      setJob(updatedJob);

      // Reset form and close dialog
      setSelectedLineItem('');
      setSelectedTeam('');
      setScheduledDate(new Date());
      setSchedulingNotes('');
      setOpenWorkItemDialog(false);

      // Show success message
      alert('Work item scheduled successfully!');
    } catch (error) {
      console.error('Error scheduling work:', error);
      alert('Failed to schedule work. Please try again.');
    }
  };

  const handleAddNote = () => {
    if (!job || !newNote.trim()) return;
    const updatedNotes = [...(job.notes || []), {
      text: newNote,
      date: new Date().toISOString(),
      createdBy: 'Admin'
    }];
    const updatedJob = { ...job, notes: updatedNotes };
    updateJob(job.id, updatedJob);
    setJob(updatedJob);
    setNewNote('');
  };

  const handleDeleteNote = (noteIndex) => {
    if (!job) return;
    const updatedNotes = (job.notes || []).filter((_, index) => index !== noteIndex);
    const updatedJob = { ...job, notes: updatedNotes };
    updateJob(job.id, updatedJob);
    setJob(updatedJob);
  };

  const handleAddPayment = () => {
    if (!job || !newPayment.amount || newPayment.amount <= 0) return;
    
    // Create payment object with timestamp
    const paymentToAdd = {
      ...newPayment,
      amount: Number(newPayment.amount),
      date: newPayment.date || new Date().toISOString().split('T')[0],
      createdAt: new Date().toISOString(),
    };
    
    // Add payment to existing payments
    const existingPayments = job.payments || [];
    const updatedPayments = [...existingPayments, paymentToAdd];
    
    // Calculate totals
    const totalAmount = job.total || 0;
    const totalPaid = updatedPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
    const remainingBalance = Math.max(0, totalAmount - totalPaid);
    
    // Determine payment status
    let paymentStatus = job.paymentStatus || 'Unpaid';
    if (remainingBalance <= 0) {
      paymentStatus = 'Paid';
    } else if (totalPaid > 0) {
      paymentStatus = 'Partially Paid';
    }
    
    // Update job
    const updatedJob = {
      ...job,
      payments: updatedPayments,
      totalPaid: totalPaid,
      remainingBalance: remainingBalance,
      paymentStatus: paymentStatus,
      updatedAt: new Date().toISOString()
    };
    
    updateJob(job.id, updatedJob);
    setJob(updatedJob);
    
    // Reset payment form
    setNewPayment({
      amount: 0,
      method: 'Cash',
      date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setOpenPaymentDialog(false);
  };

  const handleDelete = () => {
    deleteJob(id);
    navigate('/jobs');
  };

  const handleAddWork = () => {
    if (!newWork.name || !newWork.description) return;

    const workData = {
      ...newWork,
      id: Date.now().toString(),
      createdBy: 'Admin',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // Add work item to the job's workItems array
    const updatedJob = {
      ...job,
      workItems: [...(job.workItems || []), workData],
      updatedAt: new Date().toISOString()
    };
    updateJob(job.id, updatedJob);

    setIsWorkDialogOpen(false);
    setNewWork({
      name: '',
      description: '',
      status: 'Not Started',
      scheduledDate: null
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  };

  const generateInvoice = async () => {
    if (!invoiceRef.current) return;

    try {
      // US Letter size
      const pageWidth = 612;
      const pageHeight = 792;
      const margin = 24; // Reduced margins
      const contentWidth = pageWidth - 2 * margin;
      const contentHeight = pageHeight - 2 * margin;

      // Prepare the invoice content
      const invoiceContent = invoiceRef.current;
      const originalStyle = {
        width: invoiceContent.style.width,
        height: invoiceContent.style.height,
        display: invoiceContent.style.display,
        position: invoiceContent.style.position,
        left: invoiceContent.style.left,
        top: invoiceContent.style.top
      };

      invoiceContent.style.width = contentWidth + 'px';
      invoiceContent.style.height = contentHeight + 'px';
      invoiceContent.style.display = 'block';
      invoiceContent.style.position = 'absolute';
      invoiceContent.style.left = '-9999px';
      invoiceContent.style.top = '0';
      invoiceContent.style.padding = '0';
      invoiceContent.style.margin = '0';
      invoiceContent.style.boxSizing = 'border-box';
      document.body.appendChild(invoiceContent);

      // Render to canvas at scale 2 for quality
      const scale = 2;
      const canvas = await html2canvas(invoiceContent, {
        scale,
        useCORS: true,
        backgroundColor: '#ffffff',
        width: contentWidth,
        height: contentHeight,
        windowWidth: contentWidth,
        windowHeight: contentHeight,
        logging: false,
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.querySelector('[data-invoice-content]');
          if (clonedElement) {
            clonedElement.style.width = contentWidth + 'px';
            clonedElement.style.height = 'auto';
            clonedElement.style.minHeight = contentHeight + 'px';
            clonedElement.style.display = 'block';
            clonedElement.style.background = '#fff';
            clonedElement.style.padding = '24px';
            clonedElement.style.boxSizing = 'border-box';
          }
        }
      });

      // Calculate scaling to fit letter size
      const actualWidth = canvas.width / scale;
      const actualHeight = canvas.height / scale;
      
      // Scale to fit content area in points (PDF uses points)
      const scaleX = contentWidth / actualWidth;
      const scaleY = contentHeight / actualHeight;
      const scaleFactor = Math.min(scaleX, scaleY, 1); // Don't scale up, only down

      // Scale dimensions for PDF (in points)
      const scaledWidth = actualWidth * scaleFactor;
      const scaledHeight = actualHeight * scaleFactor;

      // Create PDF with letter size
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: [pageWidth, pageHeight]
      });

      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      
      // Center the image on the page
      const x = (pageWidth - scaledWidth) / 2;
      const y = margin;
      
      pdf.addImage(imgData, 'JPEG', x, y, scaledWidth, scaledHeight);
      pdf.save(`invoice-job-${id}.pdf`);
      setIsInvoiceDialogOpen(false);

      // Restore original styles
      invoiceContent.style.display = originalStyle.display;
      invoiceContent.style.position = originalStyle.position;
      invoiceContent.style.left = originalStyle.left;
      invoiceContent.style.top = originalStyle.top;
      invoiceContent.style.width = originalStyle.width;
      invoiceContent.style.height = originalStyle.height;
      document.body.removeChild(invoiceContent);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const handleAttachmentUpload = async (e) => {
    setUploadError('');
    setUploading(true);
    const file = e.target.files[0];
    if (!file) {
      setUploading(false);
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const attachments = job.attachments || [];
      const newAttachment = {
        id: Date.now().toString(),
        name: file.name,
        type: file.type.startsWith('image/') ? 'image' : 'file',
        url: event.target.result
      };
      const updatedJob = {
        ...job,
        attachments: [...attachments, newAttachment],
        updatedAt: new Date().toISOString()
      };
      updateJob(job.id, updatedJob);
      setJob(updatedJob);
      setUploading(false);
    };
    reader.onerror = () => {
      setUploadError('Failed to read file.');
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveAttachment = (attachmentId) => {
    const updatedJob = {
      ...job,
      attachments: (job.attachments || []).filter(att => att.id !== attachmentId),
      updatedAt: new Date().toISOString()
    };
    updateJob(job.id, updatedJob);
    setJob(updatedJob);
  };

  const handleEditWorkItem = (workItem) => {
    setEditingWorkItem(workItem);
    setNewWorkItem({
      name: workItem.name,
      description: workItem.description,
      quantity: workItem.quantity,
      unitPrice: workItem.unitPrice,
      status: workItem.status
    });
    setOpenWorkItemDialog(true);
  };

  const handleDragEnd = (result) => {
    if (!result.destination || !job) return;

    const items = Array.from(job.workItems);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedJob = { ...job, workItems: items };
    updateJob(job.id, updatedJob);
    setJob(updatedJob);
  };

  const handleAddDependency = (workItemId, dependsOnId) => {
    if (!job) return;
    const updatedWorkItems = job.workItems.map(item => {
      if (item.id === workItemId) {
        return {
          ...item,
          dependencies: [...(item.dependencies || []), dependsOnId]
        };
      }
      return item;
    });
    const updatedJob = { ...job, workItems: updatedWorkItems };
    updateJob(job.id, updatedJob);
    setJob(updatedJob);
  };

  const handleRemoveDependency = (workItemId, dependencyId) => {
    if (!job) return;
    const updatedWorkItems = job.workItems.map(item => {
      if (item.id === workItemId) {
        return {
          ...item,
          dependencies: (item.dependencies || []).filter(id => id !== dependencyId)
        };
      }
      return item;
    });
    const updatedJob = { ...job, workItems: updatedWorkItems };
    updateJob(job.id, updatedJob);
    setJob(updatedJob);
  };

  const renderWorkItems = () => {
    return (
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="workItems">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {(job.workItems || []).map((item, index) => (
                <Draggable key={item.id} draggableId={item.id} index={index}>
                  {(provided) => (
                    <Paper
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      elevation={1}
                      sx={{ 
                        p: 2, 
                        mb: 2, 
                        backgroundColor: 'background.paper',
                        border: 1,
                        borderColor: 'divider',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          borderColor: 'primary.main'
                        }
                      }}
                      onClick={() => navigate(`/jobs/${job.id}/work/${item.id}`)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              {item.name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditWorkItem(item);
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                              <Select
                                size="small"
                                value={item.status}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleWorkItemStatusChange(index, e.target.value);
                                }}
                                sx={{ minWidth: 150 }}
                              >
                                {WORK_ITEM_STATUSES.map(status => (
                                  <MenuItem key={status} value={status}>
                                    {status}
                                  </MenuItem>
                                ))}
                              </Select>
                            </Box>
                          </Box>
                          <Typography variant="body1" sx={{ mb: 1 }}>
                            {item.description}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Quantity: {item.quantity} | Unit Price: ${item.unitPrice}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
        {/* Header */}
        <Box sx={{ mb: 4, width: '100%' }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'stretch', sm: 'flex-start' }, 
            mb: 2,
            gap: 2,
            width: '100%'
          }}>
            <Box sx={{ width: '100%' }}>
              <Typography variant="h4" gutterBottom>
                Job #{job.jobId}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body1">{job.customerName}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body1" color="text.secondary">
                  {job.address}
                </Typography>
              </Box>
              {job.estimateId && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ReceiptIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Link
                    component="button"
                    variant="body1"
                    onClick={() => navigate(`/estimates/${job.estimateId}`)}
                    sx={{ 
                      textDecoration: 'none',
                      color: 'primary.main',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    View Original Estimate #{estimate?.estimateId || estimate?.id?.substring(0, 8) || job.estimateId?.substring(0, 8)}
                  </Link>
                </Box>
              )}
            </Box>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: { xs: 'stretch', sm: 'flex-end' }, 
              gap: 2,
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' }, 
                gap: 1, 
                alignItems: { xs: 'stretch', sm: 'flex-start' },
                width: '100%'
              }}>
                <FormControl sx={{ minWidth: { xs: '100%', sm: 150 } }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={job.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    label="Status"
                    size="small"
                  >
                    {JOB_STATUSES.map(status => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {job.estimateId && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={portalLinkCopied ? <CheckCircleIcon /> : <LinkIcon />}
                    onClick={handleCopyPortalLink}
                    fullWidth
                  >
                    {portalLinkCopied ? 'Portal Link Copied!' : 'Copy Portal Link'}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setIsDeleteDialogOpen(true)}
                  fullWidth
                >
                  Delete Job
                </Button>
              </Box>
              <Paper elevation={3} sx={{ 
                p: 2, 
                width: { xs: '100%', sm: 200 }, 
                bgcolor: 'primary.lighter', 
                border: '2px solid', 
                borderColor: 'primary.main' 
              }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {job.payments?.length > 0 ? 'Remaining Balance' : 'Job Value'}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  ${job.payments?.length > 0
                    ? (typeof job.remainingBalance === 'number'
                        ? job.remainingBalance.toFixed(2)
                        : (typeof job.total === 'number' ? job.total.toFixed(2) : '0.00'))
                    : (typeof job.total === 'number' ? job.total.toFixed(2) : '0.00')}
                </Typography>
              </Paper>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<PaymentIcon />}
                onClick={() => setOpenPaymentDialog(true)}
                fullWidth
                size="medium"
                sx={{ width: { xs: '100%', sm: 200 } }}
              >
                Add Payment
              </Button>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Created by {creatorName || job.createdBy} on {formatDate(job.createdAt)} at {formatTime(job.createdAt)}
          </Typography>
        </Box>

        {/* View Mode Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={viewMode} 
            onChange={(e, newValue) => setViewMode(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab 
              icon={<BuildIcon />} 
              label="List" 
              value="list"
            />
          </Tabs>
        </Box>

        {/* Work Items Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <BuildIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Work</Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => {
                setSelectedLineItem('');
                setSelectedTeam('');
                setScheduledDate(new Date());
                setSchedulingNotes('');
                setOpenWorkItemDialog(true);
              }}
              startIcon={<ScheduleIcon />}
            >
              Schedule Work
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />
          {(!job.workItems || job.workItems.length === 0) ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 4,
              backgroundColor: 'grey.50',
              borderRadius: 1
            }}>
              <Typography color="text.secondary" gutterBottom>
                No work items scheduled yet
                    </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click "Schedule Work" to schedule work items from the estimate
                    </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  setSelectedLineItem('');
                  setSelectedTeam('');
                  setScheduledDate(new Date());
                  setSchedulingNotes('');
                  setOpenWorkItemDialog(true);
                }}
                startIcon={<ScheduleIcon />}
              >
                Schedule Work
              </Button>
                </Box>
          ) : (
            renderWorkItems()
          )}
        </Paper>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label={`Notes (${job.notes?.length || 0})`} />
            <Tab label={`Attachments (${job.attachments?.length || 0})`} />
            <Tab label={`Payments (${job.payments?.length || 0})`} />
            <Tab label={`Inventory`} icon={<InventoryIcon />} />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ mb: 3 }}>
            {(job.notes || []).map((note, index) => (
              <Paper key={index} sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {note.text}
                </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteNote(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  By {note.createdBy} on {formatDate(note.date)} at {formatTime(note.date)}
                </Typography>
              </Paper>
            ))}
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Add New Note
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Type your note here..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              onClick={handleAddNote}
              disabled={!newNote.trim()}
              startIcon={<AddIcon />}
            >
              Add Note
            </Button>
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              component="label"
              disabled={uploading}
            >
              Upload Attachment
              <input
                type="file"
                hidden
                onChange={handleAttachmentUpload}
                accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/zip,application/x-zip-compressed,application/octet-stream,.doc,.docx,.xls,.xlsx,.pdf,.zip,.png,.jpg,.jpeg,.gif"
              />
            </Button>
            {uploading && <Typography sx={{ ml: 2 }} color="text.secondary">Uploading...</Typography>}
            {uploadError && <Typography color="error">{uploadError}</Typography>}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {(job.attachments || []).length === 0 && (
              <Typography color="text.secondary">No attachments uploaded.</Typography>
            )}
            {(job.attachments || []).map((att) => {
              const httpsUrl = ensureHttps(att.url);
              logUrl(`Job Attachment ${att.id}`, httpsUrl);
              return (
                <Paper key={att.id} sx={{ p: 2, minWidth: 180, maxWidth: 220, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                  {att.type === 'image' ? (
                    <img 
                      src={httpsUrl} 
                      alt={att.name} 
                      onLoad={() => logUrl(`Job Attachment Image Load ${att.id}`, httpsUrl)}
                      onError={(e) => {
                        console.error('Error loading attachment image:', httpsUrl);
                        logUrl(`Job Attachment Image Error ${att.id}`, httpsUrl);
                      }}
                      style={{ width: '100%', maxHeight: 120, objectFit: 'cover', marginBottom: 8 }} 
                    />
                  ) : (
                    <Box sx={{ width: '100%', textAlign: 'center', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">{att.name}</Typography>
                    </Box>
                  )}
                  <Typography variant="caption" sx={{ wordBreak: 'break-all', mb: 1 }}>{att.name}</Typography>
                  <Button size="small" color="error" onClick={() => handleRemoveAttachment(att.id)}>
                    Remove
                  </Button>
                  <Button size="small" href={httpsUrl} target="_blank" sx={{ mt: 1 }}>
                    View
                  </Button>
                </Paper>
              );
            })}
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Box sx={{ width: '100%' }}>
            {job.payments?.length > 0 ? (
              <Box>
                {job.payments.map((payment, index) => {
                  // Color mapping for payment method pills - better visibility
                  const getPaymentMethodColor = (method) => {
                    switch (method?.toLowerCase()) {
                      case 'cash':
                        return { bg: '#10B981', color: 'white' }; // Green
                      case 'check':
                        return { bg: '#3B82F6', color: 'white' }; // Blue
                      case 'card':
                        return { bg: '#8B5CF6', color: 'white' }; // Purple
                      case 'transfer':
                        return { bg: '#06B6D4', color: 'white' }; // Teal
                      default:
                        return { bg: '#6B7280', color: 'white' }; // Gray
                    }
                  };
                  const methodColor = getPaymentMethodColor(payment.method);
                  
                  return (
                    <Paper key={index} sx={{ p: 2.5, mb: 2, backgroundColor: '#1F2937', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography variant="h5" sx={{ color: '#E5E7EB', fontWeight: 600 }}>
                          ${payment.amount.toFixed(2)}
                        </Typography>
                        <Chip
                          label={payment.method || 'Unknown'}
                          sx={{
                            backgroundColor: methodColor.bg,
                            color: methodColor.color,
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            height: 32,
                            px: 1,
                            '& .MuiChip-label': {
                              px: 2,
                            },
                          }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
                        Received by {payment.createdBy} on {formatDate(payment.date)} at {formatTime(payment.date)}
                      </Typography>
                    </Paper>
                  );
                })}
                {/* Payment Summary - Enhanced visibility */}
                <Paper 
                  sx={{ 
                    mt: 3, 
                    p: 3,
                    backgroundColor: '#1F2937', // Dark card background for contrast
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                  }}
                >
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ 
                      color: '#E5E7EB',
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    Payment Summary
                  </Typography>
                  <Divider sx={{ mb: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body1" sx={{ color: '#9CA3AF', fontWeight: 500 }}>
                      Original Amount:
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#E5E7EB', fontWeight: 600 }}>
                      ${job.total?.toFixed(2) || '0.00'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body1" sx={{ color: '#9CA3AF', fontWeight: 500 }}>
                      Total Paid:
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: '#10B981', // Green for paid amount
                        fontWeight: 600,
                      }}
                    >
                      ${job.totalPaid?.toFixed(2) || '0.00'}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: '#E5E7EB',
                        fontWeight: 700,
                        fontSize: '1.1rem',
                      }}
                    >
                      Remaining Balance:
                    </Typography>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: job.remainingBalance > 0 ? '#F59E0B' : '#10B981', // Amber if balance > 0, green if paid
                        fontWeight: 700,
                      }}
                    >
                      ${job.remainingBalance?.toFixed(2) || job.total?.toFixed(2) || '0.00'}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">No payments recorded</Typography>
                <Button
                  variant="contained"
                  startIcon={<PaymentIcon />}
                  onClick={() => setOpenPaymentDialog(true)}
                  sx={{ mt: 2 }}
                >
                  Add Payment
                </Button>
              </Box>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Items Checked Out to This Job</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setSelectedInventoryItem('');
                  setCheckoutQuantity(0);
                  setCheckoutNotes('');
                  setCheckoutDialogOpen(true);
                }}
              >
                Check Out Item
              </Button>
            </Box>
            {(() => {
              // Get items checked out to this job
              const jobCheckouts = inventoryTransactions.filter(
                t => t.jobId === id && t.type === 'checkout'
              );
              const jobCheckins = inventoryTransactions.filter(
                t => t.jobId === id && t.type === 'checkin'
              );
              
              const checkedOutItems = {};
              jobCheckouts.forEach(t => {
                if (!checkedOutItems[t.itemId]) {
                  checkedOutItems[t.itemId] = 0;
                }
                checkedOutItems[t.itemId] += t.quantity || 0;
              });
              jobCheckins.forEach(t => {
                if (checkedOutItems[t.itemId]) {
                  checkedOutItems[t.itemId] -= t.quantity || 0;
                }
              });
              
              const activeCheckouts = Object.entries(checkedOutItems)
                .filter(([itemId, qty]) => qty > 0)
                .map(([itemId, qty]) => {
                  const item = inventory.find(i => i.id === itemId);
                  return { item, quantity: qty };
                })
                .filter(co => co.item);

              if (activeCheckouts.length === 0) {
                return (
                  <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    No items currently checked out to this job
                  </Typography>
                );
              }

              return (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Item</strong></TableCell>
                        <TableCell><strong>SKU</strong></TableCell>
                        <TableCell><strong>Quantity Checked Out</strong></TableCell>
                        <TableCell><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeCheckouts.map(({ item, quantity }) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>{item.sku || '-'}</TableCell>
                          <TableCell>
                            <Typography variant="body1" fontWeight="medium">
                              {quantity} {item.unit || 'units'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<RemoveIcon />}
                              onClick={() => {
                                setSelectedInventoryItem(item.id);
                                setCheckinQuantity(quantity);
                                setCheckinNotes('');
                                setCheckinDialogOpen(true);
                              }}
                            >
                              Check In
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              );
            })()}
          </Paper>
        </TabPanel>

        {/* Schedule Work Dialog */}
        <Dialog
          open={isWorkDialogOpen}
          onClose={() => setIsWorkDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add Work Item</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              {estimate && lineItems.length > 0 && (
                <FormControl fullWidth>
                  <InputLabel>Select Line Item</InputLabel>
                  <Select
                    value={selectedLineItem}
                    onChange={handleLineItemChange}
                    label="Select Line Item"
                  >
                    {lineItems.map((item, idx) => (
                      <MenuItem key={idx} value={item.name}>
                        {item.name} {item.description ? `- ${item.description}` : ''}
                      </MenuItem>
                    ))}
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              )}
              {(!estimate || lineItems.length === 0 || selectedLineItem === 'custom') && (
                <TextField
                  label="Name"
                  value={newWork.name}
                  onChange={e => setNewWork({ ...newWork, name: e.target.value })}
                  fullWidth
                />
              )}
              <TextField
                label="Description"
                value={newWork.description}
                onChange={e => setNewWork({ ...newWork, description: e.target.value })}
                multiline
                rows={4}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newWork.status}
                  onChange={e => setNewWork({ ...newWork, status: e.target.value })}
                  label="Status"
                >
                  {WORK_STATUS_OPTIONS.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <DateTimePicker
                label="Schedule Date & Time"
                value={newWork.scheduledDate}
                onChange={(newDate) => setNewWork({ ...newWork, scheduledDate: newDate })}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined'
                  }
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsWorkDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddWork} variant="contained" color="primary">
              Add Work Item
            </Button>
          </DialogActions>
        </Dialog>

        {/* Payment Dialog */}
        <Dialog
          open={openPaymentDialog}
          onClose={() => setOpenPaymentDialog(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Add Payment</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Amount"
                type="number"
                value={newPayment.amount}
                onChange={(e) => setNewPayment({ ...newPayment, amount: Number(e.target.value) })}
                fullWidth
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                }}
              />
              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={newPayment.method}
                  onChange={(e) => setNewPayment({ ...newPayment, method: e.target.value })}
                  label="Payment Method"
                >
                  {PAYMENT_METHODS.map(method => (
                    <MenuItem key={method} value={method}>
                      {method}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                type="date"
                label="Payment Date"
                value={newPayment.date}
                onChange={(e) => setNewPayment({ ...newPayment, date: e.target.value })}
                sx={{ mb: 2 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Notes"
                value={newPayment.notes}
                onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                multiline
                rows={2}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenPaymentDialog(false)}>Cancel</Button>
            <Button onClick={handleAddPayment} variant="contained" color="primary">
              Add Payment
            </Button>
          </DialogActions>
        </Dialog>

        {/* Invoice Preview Dialog */}
        <Dialog
          open={isInvoiceDialogOpen}
          onClose={() => setIsInvoiceDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Invoice for Job #{id}</span>
              {job.paymentStatus && (
                <Chip
                  label={
                    job.paymentStatus === 'paid' || job.paymentStatus === 'Paid'
                      ? 'Paid'
                      : job.paymentStatus === 'pending'
                      ? 'Payment Pending'
                      : 'Unpaid'
                  }
                  color={
                    job.paymentStatus === 'paid' || job.paymentStatus === 'Paid'
                      ? 'success'
                      : job.paymentStatus === 'pending'
                      ? 'warning'
                      : 'default'
                  }
                  size="small"
                />
              )}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ p: 2 }}>
              <Paper ref={invoiceRef} sx={{ p: 0, mb: 3, boxShadow: 0 }}>
                <div 
                  data-invoice-content
                  style={{ 
                    width: 564, 
                    minHeight: 744, 
                    padding: 24, 
                    fontFamily: 'Arial, sans-serif', 
                    color: '#222', 
                    background: '#fff', 
                    boxSizing: 'border-box' 
                  }}
                >
                  {/* Header Section */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
                    {/* Left: Company Info */}
                    <div style={{ flex: 1 }}>
                      <h1 style={{ margin: '0 0 8px 0', fontSize: 20, fontWeight: 'bold', color: '#222', textTransform: 'uppercase' }}>
                        {companyProfile?.companyName?.toUpperCase() || 'YOUR COMPANY INC.'}
                      </h1>
                      <div style={{ fontSize: 12, color: '#555', lineHeight: 1.6 }}>
                        {companyProfile?.address && <>{companyProfile.address}<br /></>}
                        {companyProfile?.phone && <>{companyProfile.phone}<br /></>}
                        {companyProfile?.email && <>{companyProfile.email}<br /></>}
                        {companyProfile?.website && <>{companyProfile.website}<br /></>}
                        {companyProfile?.licenseNumber && <>License #: {companyProfile.licenseNumber}</>}
                      </div>
                    </div>
                    
                    {/* Right: Logo and Title */}
                    <div style={{ textAlign: 'right', flex: 1 }}>
                      {/* Logo */}
                      {companyProfile?.logo ? (
                        <div style={{ 
                          marginLeft: 'auto', 
                          marginBottom: 12,
                          display: 'flex',
                          justifyContent: 'flex-end'
                        }}>
                          <img 
                            src={companyProfile.logo.startsWith('data:') ? companyProfile.logo : ensureHttps(companyProfile.logo)}
                            alt="Company logo"
                            style={{
                              maxWidth: 120,
                              maxHeight: 60,
                              objectFit: 'contain'
                            }}
                          />
                        </div>
                      ) : (
                        <div style={{ 
                          width: 120, 
                          height: 60, 
                          border: '1px dashed #ccc', 
                          borderRadius: 4, 
                          marginLeft: 'auto', 
                          marginBottom: 12,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 10,
                          color: '#999'
                        }}>
                          <div style={{ fontSize: 20, marginBottom: 4 }}>☁</div>
                          <div>Upload Logo</div>
                        </div>
                      )}
                      
                      {/* INVOICE Title */}
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1976d2', lineHeight: 1.2, letterSpacing: 1 }}>
                          INVOICE
                        </div>
                      </div>
                      
                      {/* Invoice Details */}
                      <div style={{ fontSize: 11, textAlign: 'right', lineHeight: 1.8 }}>
                        <div>
                          <span style={{ color: '#1976d2', fontWeight: 'bold' }}>Invoice #</span>
                          <span style={{ marginLeft: 8, color: '#222' }}>INV-{job.jobId || id}</span>
                        </div>
                        <div>
                          <span style={{ color: '#1976d2', fontWeight: 'bold' }}>Invoice date</span>
                          <span style={{ marginLeft: 8, color: '#222' }}>{new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}</span>
                        </div>
                        <div>
                          <span style={{ color: '#1976d2', fontWeight: 'bold' }}>Due date</span>
                          <span style={{ marginLeft: 8, color: '#222' }}>
                            {job.dueDate 
                              ? new Date(job.dueDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
                              : new Date(new Date().getTime() + 14 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bill To Section */}
                  <div style={{ marginBottom: 20 }}>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: 14, fontWeight: 'bold', color: '#1976d2' }}>
                      Bill To:
                    </h3>
                    <div style={{ fontSize: 12, color: '#222', lineHeight: 1.6 }}>
                      <div style={{ fontWeight: 'bold' }}>{job.customerName}</div>
                      {job.address && <div>{job.address}</div>}
                      {estimate?.contactInfo?.phone && <div>Phone: {estimate.contactInfo.phone}</div>}
                      {estimate?.contactInfo?.email && <div>Email: {estimate.contactInfo.email}</div>}
                    </div>
                  </div>

                  {/* Line Items Table */}
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                    <thead>
                      <tr style={{ background: '#1976d2' }}>
                        <th style={{ padding: '10px 8px', textAlign: 'left', color: '#fff', fontSize: 11, fontWeight: 'bold' }}>QTY</th>
                        <th style={{ padding: '10px 8px', textAlign: 'left', color: '#fff', fontSize: 11, fontWeight: 'bold' }}>Description</th>
                        <th style={{ padding: '10px 8px', textAlign: 'right', color: '#fff', fontSize: 11, fontWeight: 'bold' }}>Unit Price</th>
                        <th style={{ padding: '10px 8px', textAlign: 'right', color: '#fff', fontSize: 11, fontWeight: 'bold' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {((job.workItems || []).length > 0 ? job.workItems : (estimate?.lineItems || [])).map((item, idx) => {
                        const qty = item.quantity || item.qty || 1;
                        const unitPrice = item.unitPrice !== undefined ? item.unitPrice : (item.expenses && item.expenses[0]?.amount ? item.expenses[0].amount : 0);
                        const amount = item.unitPrice !== undefined && item.quantity !== undefined
                          ? (item.unitPrice * item.quantity)
                          : (item.expenses
                              ? item.expenses.reduce((sum, exp) => sum + (exp.quantity || 1) * exp.amount, 0)
                              : 0);
                        return (
                          <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: '10px 8px', fontSize: 11, textAlign: 'left' }}>{qty.toFixed(2)}</td>
                            <td style={{ padding: '10px 8px', fontSize: 11, textAlign: 'left' }}>{item.description || item.name || 'Work Item'}</td>
                            <td style={{ padding: '10px 8px', fontSize: 11, textAlign: 'right' }}>${unitPrice.toFixed(2)}</td>
                            <td style={{ padding: '10px 8px', fontSize: 11, textAlign: 'right' }}>${amount.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Summary Section */}
                  {(() => {
                    const subtotal = ((job.workItems || []).length > 0 ? job.workItems : (estimate?.lineItems || [])).reduce((sum, item) => {
                      if (item.unitPrice !== undefined && item.quantity !== undefined) {
                        return sum + (item.unitPrice * item.quantity);
                      } else if (item.expenses) {
                        return sum + item.expenses.reduce((expSum, exp) => expSum + (exp.quantity || 1) * exp.amount, 0);
                      }
                      return sum;
                    }, 0);
                    // Use estimate tax rate if set, otherwise fall back to company default
                    const taxRate = estimate?.taxRate || companyProfile?.defaultTaxRate || 0;
                    const taxAmount = subtotal * (taxRate / 100);
                    const total = subtotal + taxAmount;
                    
                    return (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
                        <div style={{ width: 200, fontSize: 11 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                            <span>Subtotal</span>
                            <span>${subtotal.toFixed(2)}</span>
                          </div>
                          {taxRate > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                              <span>Sales Tax ({taxRate}%)</span>
                              <span>${taxAmount.toFixed(2)}</span>
                            </div>
                          )}
                          <div style={{ borderTop: '1px solid #ddd', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#1976d2', fontWeight: 'bold' }}>Total (USD)</span>
                            <span style={{ color: '#1976d2', fontWeight: 'bold' }}>${(typeof job.total === 'number' ? job.total : total).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Footer Section */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'auto', paddingTop: 20 }}>
                    {/* Terms and Conditions */}
                    <div style={{ flex: 1, fontSize: 10, color: '#555' }}>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: 11, fontWeight: 'bold', color: '#1976d2' }}>
                        Terms and Conditions
                      </h4>
                      <div style={{ lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                        {companyProfile?.defaultTerms ? (
                          companyProfile.defaultTerms
                        ) : (
                          <>
                            <div>Payment is due in 14 days.</div>
                            <div>Please make checks payable to: {companyProfile?.companyName || 'Your Company Inc.'}</div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Customer Signature */}
                    <div style={{ flex: 1, textAlign: 'center', paddingTop: 40 }}>
                      <div style={{ borderTop: '1px solid #ddd', width: 200, margin: '0 auto 8px auto', paddingTop: 8 }}></div>
                      <div style={{ fontSize: 10, color: '#1976d2', fontWeight: 'bold' }}>
                        Customer Signature
                      </div>
                    </div>
                  </div>
                </div>
              </Paper>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsInvoiceDialogOpen(false)}>Close</Button>
            
            {/* Pay Online Button - only show if not paid */}
            {job.paymentStatus !== 'paid' && job.paymentStatus !== 'Paid' && (
              <Button
                variant="contained"
                color="success"
                onClick={handlePayOnline}
                disabled={isProcessingPayment}
                startIcon={<PaymentIcon />}
                sx={{ mr: 1 }}
              >
                {isProcessingPayment ? 'Processing...' : 'Pay Online'}
              </Button>
            )}
            
            <Button
              variant="contained"
              onClick={generateInvoice}
              startIcon={<ReceiptIcon />}
            >
              Download PDF
            </Button>
          </DialogActions>
          
          {paymentError && (
            <Box sx={{ px: 3, pb: 2 }}>
              <Alert severity="error" onClose={() => setPaymentError('')}>
                {paymentError}
              </Alert>
            </Box>
          )}
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
        >
          <DialogTitle>Delete Job</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this job? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Work Item Dialog */}
        <Dialog
          open={openWorkItemDialog}
          onClose={() => {
            setOpenWorkItemDialog(false);
            setSelectedLineItem('');
            setSelectedTeam('');
            setScheduledDate(new Date());
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Schedule Work</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Line Item</InputLabel>
                <Select
                  value={selectedLineItem}
                  onChange={(e) => setSelectedLineItem(e.target.value)}
                  label="Line Item"
                >
                  {estimate?.lineItems?.map((item, index) => {
                    const isAlreadyScheduled = job.workItems?.some(
                      workItem => workItem.estimateId === job.estimateId && workItem.lineItemIndex === index
                    );
                    if (isAlreadyScheduled) return null; // Only show unscheduled line items
                    return (
                      <MenuItem 
                        key={index} 
                        value={index}
                      >
                        {item.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Team</InputLabel>
                <Select
                  value={selectedTeam}
                  onChange={(e) => setSelectedTeam(e.target.value)}
                  label="Team"
                >
                  {teams.map((team) => (
                    <MenuItem key={team.id} value={team.id}>
                      {team.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <DateTimePicker
                label="Schedule Date & Time"
                value={scheduledDate}
                onChange={(newValue) => setScheduledDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined'
                  }
                }}
              />

              {selectedLineItem !== '' && estimate?.lineItems?.[selectedLineItem] && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Line Item Details
                  </Typography>
                  <Typography variant="body2">
                    Description: {estimate.lineItems[selectedLineItem].description}
                  </Typography>
                  <Typography variant="body2">
                    Quantity: {estimate.lineItems[selectedLineItem].quantity}
                  </Typography>
                  <Typography variant="body2">
                    Unit Price: ${estimate.lineItems[selectedLineItem].unitPrice}
                  </Typography>
                  <Typography variant="body2">
                    Total: ${(estimate.lineItems[selectedLineItem].quantity || 0) * (estimate.lineItems[selectedLineItem].unitPrice || 0)}
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setOpenWorkItemDialog(false);
              setSelectedLineItem('');
              setSelectedTeam('');
              setScheduledDate(new Date());
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleScheduleWork} 
              variant="contained" 
              color="primary"
              disabled={selectedLineItem === '' || selectedTeam === '' || !scheduledDate}
            >
              Schedule Work
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dependency Dialog */}
        <Dialog
          open={dependencyDialogOpen}
          onClose={() => {
            setDependencyDialogOpen(false);
            setSelectedWorkItem(null);
            setSelectedDependency(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add Dependency</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Select Work Item</InputLabel>
                <Select
                  value={selectedDependency || ''}
                  onChange={(e) => setSelectedDependency(e.target.value)}
                  label="Select Work Item"
                >
                  {(job.workItems || [])
                    .filter(item => item.id !== selectedWorkItem?.id)
                    .map(item => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setDependencyDialogOpen(false);
              setSelectedWorkItem(null);
              setSelectedDependency(null);
            }}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedWorkItem && selectedDependency) {
                  handleAddDependency(selectedWorkItem.id, selectedDependency);
                }
                setDependencyDialogOpen(false);
                setSelectedWorkItem(null);
                setSelectedDependency(null);
              }}
              variant="contained"
              color="primary"
            >
              Add Dependency
            </Button>
          </DialogActions>
        </Dialog>

        {/* Checkout Dialog */}
        <Dialog
          open={checkoutDialogOpen}
          onClose={() => {
            setCheckoutDialogOpen(false);
            setSelectedInventoryItem('');
            setCheckoutQuantity(0);
            setCheckoutNotes('');
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Check Out Inventory Item</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Select Item</InputLabel>
                <Select
                  value={selectedInventoryItem}
                  onChange={(e) => setSelectedInventoryItem(e.target.value)}
                  label="Select Item"
                >
                  {inventory.map(item => {
                    const totalQty = item.totalQuantity || item.quantity || 0;
                    const checkedOut = item.checkedOutQuantity || 0;
                    const available = totalQty - checkedOut;
                    return (
                      <MenuItem key={item.id} value={item.id} disabled={available === 0}>
                        {item.name} - {available} {item.unit || 'units'} available
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              {selectedInventoryItem && (() => {
                const item = inventory.find(i => i.id === selectedInventoryItem);
                const totalQty = item?.totalQuantity || item?.quantity || 0;
                const checkedOut = item?.checkedOutQuantity || 0;
                const available = totalQty - checkedOut;
                return (
                  <>
                    <TextField
                      fullWidth
                      label="Quantity"
                      type="number"
                      value={checkoutQuantity}
                      onChange={(e) => setCheckoutQuantity(parseFloat(e.target.value) || 0)}
                      inputProps={{ min: 0, max: available, step: 0.01 }}
                      helperText={`Available: ${available} ${item?.unit || 'units'}`}
                    />
                    {checkoutQuantity > available && (
                      <Typography color="error" variant="body2">
                        Cannot check out more than available
                      </Typography>
                    )}
                  </>
                );
              })()}
              <TextField
                fullWidth
                label="Notes (optional)"
                value={checkoutNotes}
                onChange={(e) => setCheckoutNotes(e.target.value)}
                multiline
                rows={2}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setCheckoutDialogOpen(false);
              setSelectedInventoryItem('');
              setCheckoutQuantity(0);
              setCheckoutNotes('');
            }}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                try {
                  const item = inventory.find(i => i.id === selectedInventoryItem);
                  const totalQty = item?.totalQuantity || item?.quantity || 0;
                  const checkedOut = item?.checkedOutQuantity || 0;
                  const available = totalQty - checkedOut;
                  
                  if (checkoutQuantity <= 0 || checkoutQuantity > available) {
                    alert('Invalid quantity');
                    return;
                  }
                  
                  await checkoutInventoryItem(selectedInventoryItem, id, checkoutQuantity, checkoutNotes);
                  setCheckoutDialogOpen(false);
                  setSelectedInventoryItem('');
                  setCheckoutQuantity(0);
                  setCheckoutNotes('');
                } catch (error) {
                  alert(error.message || 'Failed to check out item');
                }
              }}
              variant="contained"
              disabled={!selectedInventoryItem || checkoutQuantity <= 0}
            >
              Check Out
            </Button>
          </DialogActions>
        </Dialog>

        {/* Checkin Dialog */}
        <Dialog
          open={checkinDialogOpen}
          onClose={() => {
            setCheckinDialogOpen(false);
            setSelectedInventoryItem('');
            setCheckinQuantity(0);
            setCheckinNotes('');
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Check In Inventory Item</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {selectedInventoryItem && (() => {
                const item = inventory.find(i => i.id === selectedInventoryItem);
                const jobCheckouts = inventoryTransactions.filter(
                  t => t.itemId === selectedInventoryItem && t.jobId === id && t.type === 'checkout'
                );
                const jobCheckins = inventoryTransactions.filter(
                  t => t.itemId === selectedInventoryItem && t.jobId === id && t.type === 'checkin'
                );
                const totalCheckedOut = jobCheckouts.reduce((sum, t) => sum + (t.quantity || 0), 0);
                const totalCheckedIn = jobCheckins.reduce((sum, t) => sum + (t.quantity || 0), 0);
                const currentlyCheckedOut = totalCheckedOut - totalCheckedIn;
                
                return (
                  <>
                    <Typography variant="body1">
                      <strong>{item?.name}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Currently checked out: {currentlyCheckedOut} {item?.unit || 'units'}
                    </Typography>
                    <TextField
                      fullWidth
                      label="Quantity to Check In"
                      type="number"
                      value={checkinQuantity}
                      onChange={(e) => setCheckinQuantity(parseFloat(e.target.value) || 0)}
                      inputProps={{ min: 0, max: currentlyCheckedOut, step: 0.01 }}
                      helperText={`Maximum: ${currentlyCheckedOut} ${item?.unit || 'units'}`}
                    />
                    {checkinQuantity > currentlyCheckedOut && (
                      <Typography color="error" variant="body2">
                        Cannot check in more than what's checked out
                      </Typography>
                    )}
                  </>
                );
              })()}
              <TextField
                fullWidth
                label="Notes (optional)"
                value={checkinNotes}
                onChange={(e) => setCheckinNotes(e.target.value)}
                multiline
                rows={2}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setCheckinDialogOpen(false);
              setSelectedInventoryItem('');
              setCheckinQuantity(0);
              setCheckinNotes('');
            }}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                try {
                  if (checkinQuantity <= 0) {
                    alert('Please enter a valid quantity');
                    return;
                  }
                  
                  await checkinInventoryItem(selectedInventoryItem, id, checkinQuantity, checkinNotes);
                  setCheckinDialogOpen(false);
                  setSelectedInventoryItem('');
                  setCheckinQuantity(0);
                  setCheckinNotes('');
                } catch (error) {
                  alert(error.message || 'Failed to check in item');
                }
              }}
              variant="contained"
              disabled={!selectedInventoryItem || checkinQuantity <= 0}
            >
              Check In
            </Button>
          </DialogActions>
        </Dialog>
        </Box>

        {/* Snackbar for portal link copied notification */}
        <Snackbar
          open={portalLinkCopied}
          autoHideDuration={3000}
          onClose={() => setPortalLinkCopied(false)}
          message="Portal link copied to clipboard!"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </LocalizationProvider>
    );
  }